import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { AppleMap } from "capitalrx-components-2.0/lib/FindBestPriceTool";
import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import {
  LocationGeolocate,
  Pharmacy,
  useListFavoritePharmaciesQuery,
  useListPharmaciesQuery,
  useLocationGeolocateQuery,
} from "@/App/api/pharmacy";
import { ContentPanel, MapLocation, NoResults, PageWrapper, Pager, PharmacyTile, ThemedButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";
import { getPharmacyDescription } from "@/App/utils/utils";

import { NearbyPharmacySearchForm } from "./NearbyPharmacySearchForm";

const ZIP_REGEX = new RegExp("d{5}");

export const NearbyPharmacyResultsPage: FC = () => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const searchNear = searchParams.get("searchNear");
  const searchParamLatitude = searchParams.get("latitude");
  const searchParamLongitude = searchParams.get("longitude");
  const navigate = useNavigateClient();
  const [pageNumber, setPageNumber] = useState(1);
  const { data: favoritePharmacies } = useListFavoritePharmaciesQuery();

  const { isCompressed } = useResponsive({
    isCompressed: {
      desktop: false,
      mobile: true,
    },
  });

  useEffect(() => {
    if (!searchNear && !searchParamLatitude && !searchParamLongitude) {
      navigate("/nearby-pharmacy");
    }
  }, [searchNear, searchParamLatitude, searchParamLongitude]);

  const onPharmacySelected = useCallback(
    (pharmacy: Pharmacy) => {
      navigate({
        pathname: `/nearby-pharmacy/pharmacy-details/${pharmacy.provider_id}`,
        search: searchParams.toString(),
      });
    },
    [searchParams],
  );

  const geolocateQueryFilters: Partial<LocationGeolocate> = useMemo(() => {
    if (searchParamLatitude && searchParamLongitude) {
      return {
        latitude: decodeURIComponent(searchParamLatitude),
        longitude: decodeURIComponent(searchParamLongitude),
      };
    } else {
      const decoded = decodeURIComponent(searchNear);
      if (ZIP_REGEX.test(decoded)) {
        return { zip_code: decoded };
      } else {
        return { search_address: decoded };
      }
    }
  }, [searchNear, searchParamLatitude, searchParamLongitude]);

  const { data: locationData, isLoading: isLoadingLocationData } = useLocationGeolocateQuery(geolocateQueryFilters, !!searchNear);

  const latitude = searchParamLatitude || locationData?.latitude;
  const longitude = searchParamLongitude || locationData?.longitude;

  const {
    data: pharmacyData,
    isLoading: isLoadingPharmacyData,
    isRefetching: isRefetchingPharmacies,
  } = useListPharmaciesQuery(
    {
      latitude: latitude,
      longitude: longitude,
      page_number: pageNumber,
      favorite_pharmacy_provider_ids_display_first: (favoritePharmacies?.results || []).map((p) => p.pharmacy.provider_id),
    },
    (!!favoritePharmacies && !!locationData) || (!!searchParamLatitude && !!searchParamLongitude),
  );

  const isFetchingPharmacyData = useMemo(() => {
    if (searchParamLatitude && searchParamLongitude) {
      return false;
    } else if (searchNear) {
      return isLoadingLocationData !== false;
    } else {
      return true;
    }
  }, [searchParamLatitude, searchParamLongitude, searchNear, isLoadingLocationData]);

  const mapLocations: MapLocation[] = useMemo(() => {
    return (pharmacyData?.results || [])
      .filter((pharmacy) => {
        return pharmacy.pharmacy.pharmacy_display_rules?.display_on_map !== "never";
      })
      .map((pharmacy) => {
        return {
          latitude: pharmacy.pharmacy.latitude,
          longitude: pharmacy.pharmacy.longitude,
          displayDescription: getPharmacyDescription(
            pharmacy.pharmacy,
            translate("NEARBY_PHARMACY.nearbyPharmacyResultsPage.miles", { count: pharmacy?.pharmacy?.distance || 2 }),
          ),
          displayTitle: pharmacy.pharmacy.name,
        };
      });
  }, [pharmacyData]);

  return (
    <PageWrapper>
      <ContentPanel title={translate("NEARBY_PHARMACY.nearbyPharmacyResultsPage.nearbyPharmacies")}>
        <Stack>
          <NearbyPharmacySearchForm />
          <LoadingGroup loading={isFetchingPharmacyData}>
            <LoadingGroup.Placeholder
              fallback={
                <Shelf>
                  <Spinner size="large" />
                </Shelf>
              }
            >
              <Grid>
                {!isLoadingPharmacyData && (pharmacyData?.results || []).length === 0 && (
                  <Grid.Span columns={12}>
                    <NoResults message={translate("NEARBY_PHARMACY.nearbyPharmacyResultsPage.noPharmaciesFound")} />
                  </Grid.Span>
                )}

                <Grid.Span columns={isCompressed ? 12 : 6}>
                  <Stack spacing="small">
                    <LoadingGroup loading={isLoadingPharmacyData || isRefetchingPharmacies}>
                      <LoadingGroup.Placeholder
                        fallback={
                          <Shelf>
                            <Spinner size="large" />
                          </Shelf>
                        }
                      >
                        {pharmacyData?.results?.map((pharmacy, idx) => {
                          return (
                            <PharmacyTile
                              key={pharmacy.pharmacy.provider_id}
                              pharmacy={pharmacy?.pharmacy}
                              isLoading={isLoadingPharmacyData}
                              userLocation={locationData}
                              index={idx + 10 * (pageNumber - 1)}
                              button={
                                <ThemedButton
                                  testId={`go-to-pharmacy-${pharmacy.pharmacy.provider_id}`}
                                  onClick={() => onPharmacySelected(pharmacy.pharmacy)}
                                  appearance="primary"
                                  icon="chevron-right"
                                  iconPosition="right"
                                >
                                  {translate("NEARBY_PHARMACY.nearbyPharmacyResultsPage.view")}
                                </ThemedButton>
                              }
                            />
                          );
                        })}
                      </LoadingGroup.Placeholder>
                    </LoadingGroup>
                  </Stack>
                </Grid.Span>

                <Grid.Span columns={isCompressed ? 12 : 6}>
                  {mapLocations.length !== 0 && (
                    <AppleMap
                      style={{ height: isCompressed ? "400px" : "100%" }}
                      locations={mapLocations.map((location) => ({
                        latitude: parseFloat(location.latitude),
                        longitude: parseFloat(location.longitude),
                        title: location.displayTitle,
                        data: {
                          displayDescription: location.displayDescription,
                        },
                      }))}
                      renderCalloutContent={(location) => <div>{location.data.displayDescription}</div>}
                    />
                  )}
                </Grid.Span>

                {(pharmacyData?.results || []).length !== 0 && (
                  <Grid.Span columns={12}>
                    <Pager
                      resultCount={pharmacyData?.result_count}
                      resultsPerPage={10}
                      pageNumber={pageNumber}
                      setPageNumber={setPageNumber}
                      disabled={isLoadingPharmacyData || isRefetchingPharmacies}
                    />
                  </Grid.Span>
                )}
              </Grid>
            </LoadingGroup.Placeholder>
          </LoadingGroup>
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
